import { useTranslation } from "react-i18next";

import { ApiError, ForbiddenError } from "@/api/errors";

import { BpmnFileField } from "./bpmn-file-field";
import { BpmnPreview } from "./bpmn-preview";
import { InlineConfirm } from "./inline-confirm";
import { useBpmnFileSelection } from "./use-bpmn-file-selection";
import { DiagramRestoreError, useReplaceDiagram } from "./use-replace-diagram";

export type ImportDiagramPanelProps = {
  readonly processModelId: string;
  readonly primaryFileName: string;
  readonly currentProcessId: string | null;
  readonly onClose: () => void;
};

/**
 * 「取り込み」の確認欄。書き出した図を外部で直して戻す操作（「書き出し」の逆）。
 *
 * 差し替えると利用者の手では元に戻せないので、取り込む図をプレビューで見せてから
 * 確定させる。図を描けないファイル（レイアウト情報が無い等）は、差し替えると
 * この画面で表示も編集もできなくなるので確定させない。実行プロセス id が変わる
 * 場合は、設定も更新されることを先に知らせる。
 * 「差し替える」は赤にしない（赤は削除だけに使う）。
 */
export function ImportDiagramPanel({
  processModelId,
  primaryFileName,
  currentProcessId,
  onClose,
}: ImportDiagramPanelProps) {
  const { t } = useTranslation("common");
  const replace = useReplaceDiagram();
  const selection = useBpmnFileSelection();
  const { ready } = selection;

  return (
    <InlineConfirm
      label={t("processDesign.importDiagram.title")}
      confirmLabel={t("processDesign.importDiagram.submit")}
      cancelLabel={t("processDesign.importDiagram.cancel")}
      // 図を描けたと分かるまで確定させない（描けない図に差し替えると、この画面で
      // 表示も編集もできなくなる）
      canConfirm={ready !== null && selection.previewStatus === "ok"}
      isPending={replace.isPending}
      error={replace.isError ? errorMessage(replace.error) : null}
      onCancel={onClose}
      onConfirm={() => {
        if (ready === null) return;
        replace.mutate(
          {
            processModelId,
            primaryFileName,
            xml: ready.xml,
            processId: ready.meta.processId,
            previousProcessId: currentProcessId,
          },
          { onSuccess: onClose },
        );
      }}
    >
      <p className="m-0 font-medium">
        {t("processDesign.importDiagram.title")}
      </p>
      <p className="m-0">
        {t("processDesign.importDiagram.description", {
          file: primaryFileName,
        })}
      </p>
      <BpmnFileField
        selected={selection.file}
        onSelect={(picked) => {
          // 前のファイルでの失敗は、選び直したら当てはまらない
          replace.reset();
          void selection.select(picked);
        }}
        onClear={() => {
          replace.reset();
          selection.clear();
        }}
      />
      {ready === null ? null : (
        <>
          <p className="text-muted-foreground m-0 text-xs">
            {t("processDesign.primaryProcess")}:{" "}
            <span className="font-mono">{ready.meta.processId}</span>
          </p>
          {ready.meta.processId === currentProcessId ? null : (
            <p className="border-border bg-muted m-0 rounded-md border px-3 py-2 text-xs">
              {currentProcessId === null
                ? t("processDesign.importDiagram.processIdSet", {
                    to: ready.meta.processId,
                  })
                : t("processDesign.importDiagram.processIdChanged", {
                    from: currentProcessId,
                    to: ready.meta.processId,
                  })}
            </p>
          )}
          {selection.previewStatus === "failed" ? (
            <p role="alert" className="text-destructive m-0 text-xs">
              {t("processDesign.importDiagram.previewFailed")}
            </p>
          ) : null}
          <BpmnPreview
            // 選ぶたびに作り直す（一度描けなかった部品は描き直さないため）
            key={selection.version}
            xml={ready.xml}
            label={t("processDesign.importDiagram.preview")}
            onRendered={selection.markPreviewRendered}
            onRenderError={selection.markPreviewFailed}
          />
        </>
      )}
    </InlineConfirm>
  );

  function errorMessage(error: Error): string {
    // 保存に失敗し、書き換わった可能性のある実行プロセスの設定も戻せなかった
    if (error instanceof DiagramRestoreError) {
      return t("processDesign.importDiagram.restoreFailed");
    }
    if (error instanceof ForbiddenError) {
      return t("processDesign.importDiagram.forbidden");
    }
    // backend の BPMN 検証で弾かれた（プロセス id が他のモデルと重なっている等）。
    // 理由は backend の説明にしか無いので、そのまま添える
    if (
      error instanceof ApiError &&
      error.errorCode === "process_model_file_invalid"
    ) {
      return t("processDesign.importDiagram.fileInvalid", {
        detail: error.message,
      });
    }
    return t("processDesign.importDiagram.failed");
  }
}
